import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { setCredentials, clearCredentials } from './authSlice';

/**
 * Base query — reads the short-lived access token from Redux state (memory).
 * credentials:"include" ensures the httpOnly refresh-token cookie is always
 * sent by the browser automatically.
 */
const baseQueryWithAuth = fetchBaseQuery({
  baseUrl: process.env.REACT_APP_API_BASE_URL,
  credentials: 'include',
  prepareHeaders: (headers, { getState }) => {
    const token = getState().auth.token;
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }
    return headers;
  },
});

/**
 * Dedicated base query for the refresh-token endpoint only.
 * - credentials:"include" → browser sends the httpOnly refresh cookie
 * - No Authorization header (expired access token must NOT be forwarded)
 * - Empty body (backend requirement)
 */
const baseQueryForRefresh = fetchBaseQuery({
  baseUrl: process.env.REACT_APP_API_BASE_URL,
  credentials: 'include',
  // prepareHeaders intentionally omitted — no Authorization header
});

/**
 * Mutex-style guard so that multiple concurrent 401 responses only trigger
 * ONE refresh call.  All other requests wait for the same promise to settle.
 */
let refreshingPromise = null;

const baseQueryWithReauth = async (args, api, extraOptions) => {
  let result = await baseQueryWithAuth(args, api, extraOptions);

  if (result?.error?.status === 401) {
    // --- Check absolute session expiry first ---
    const maxSessionUntil = api.getState().auth.maxSessionUntil;
    if (maxSessionUntil && new Date() >= new Date(maxSessionUntil)) {
      api.dispatch(clearCredentials());
      window.location.replace('/login?reason=session_expired');
      return result;
    }

    // --- Attempt token refresh (deduplicated) ---
    // prettier-ignore
    if (!refreshingPromise) {
      refreshingPromise = baseQueryForRefresh(
        { url: 'api/v1/en/admin/refresh-token', method: 'POST', body: {} },
        api,
        extraOptions
      )
        .then((refreshResult) => {
          if (refreshResult?.data?.data?.success === 1) {
            const { token, maxSessionUntil: newMaxSession } = refreshResult.data.data.data;
            api.dispatch(setCredentials({ token, maxSessionUntil: newMaxSession }));
            return token;
          }
          // Refresh failed — clear everything
          api.dispatch(clearCredentials());
          return null;
        })
        .finally(() => {
          refreshingPromise = null;
        });
    }

    const newToken = await refreshingPromise;

    if (newToken) {
      // Retry the original request with the new token now in Redux state
      result = await baseQueryWithAuth(args, api, extraOptions);
    } else {
      window.location.replace('/login?reason=session_expired');
    }
  }

  return result;
};

export const apiSlice = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithReauth,
  refetchOnFocus: false,
  refetchOnReconnect: true,
  tagTypes: [
    'Login',
    'PermissionRoles',
    'Students',
    'Student',
    'Dashboard',
    'Courses',
    'Course',
    'Employees',
    'Employee',
    'Packages',
    'Package',
    'Countries',
    'Timezones',
    'LiveClass',
    'CourseStaff',
    'MockTests',
    'PTEMockTests',
    'TeacherAvailability',
    'B2BInstitutions',
    'B2BSubscriptions',
    'Branches',
    'Branch',
    'AdminRoles',
    'SubAdmins',
    'InstitutionCourseStaff',
  ],
  endpoints: (builder) => ({
    // your endpoints here
  }),
});
